import type { CalcCheck, CalcInputs, CalcStructuredValue } from "../../types/calc";

export type StructuralHelpFixture = {
  calculatorId: string;
  mark: string;
  callout: string;
  inputs: CalcInputs;
  calculationKind: string;
  designMethod: string;
  checks: CalcCheck[];
  governing: string;
  assumptions: string[];
  limitations: string[];
  intermediates: Record<string, CalcStructuredValue>;
  reactions?: Array<{
    label: string;
    positionIn: number;
    byType: Record<string, number>;
  }>;
};

const check = (
  limitState: string,
  demand: number | null,
  capacity: number | null,
  units: string | null,
  ndsRef: string,
  governingCombo: string,
  passes = true,
): CalcCheck => ({
  limitState,
  passes,
  demand,
  capacity,
  units,
  dcr: demand != null && capacity != null && capacity !== 0 ? demand / capacity : null,
  governingCombo,
  ndsRef,
  threshold: 1,
});

export const STRUCTURAL_HELP_FIXTURES: Record<string, StructuralHelpFixture> = {
  steel_beam_aisc: {
    calculatorId: "steel_beam_aisc",
    mark: "SB-1",
    callout: "W18X50 · ASTM A992",
    inputs: {
      steel_shape_key: "W18X50",
      steel_grade_key: "a992",
      design_method: "ASD",
      unbraced_length_ft: 0,
      cb: 1,
      required_moment_kipft: 184,
      required_shear_kip: 30,
      service_span_ft: 20,
      service_uniform_load_plf: 1000,
      deflection_limit_ratio: 360,
    },
    calculationKind: "member_design",
    designMethod: "ASD",
    checks: [
      check("major_axis_flexure", 184, 251.99600798403196, "kip-ft", "AISC 360-22 Chapter F, Section F2", "ASD"),
      check("web_shear", 30, 127.79999999999998, "kip", "AISC 360-22 Chapter G, Section G2", "ASD"),
      check("service_deflection", 0.15517241379310343, 0.6666666666666666, "in", "ANSI/AISC 360-22 L3; simple-span elastic beam theory", "Service"),
    ],
    governing: "major_axis_flexure",
    assumptions: ["Loads pass through the shear center and the compression flange bracing matches the entered Lb."],
    limitations: [
      "Concentrated-load local checks, copes/holes, web yielding/crippling, fatigue, torsion, fire, seismic provisions, connections, vibration, ponding, camber, and composite action require separate verification.",
      "Rolled I-shapes (W, S, M, HP) are covered when compact; noncompact/slender sections, channels, tees, angles, HSS, and built-up members are outside v1 scope.",
    ],
    intermediates: { Mn_kipft: 420.8333333333333, Lp_in: 69.93758131362566, Lr_in: 203.34717057951497, Cv1: 1, service_deflection_in: 0.15517241379310343 },
  },
  steel_column_aisc: {
    calculatorId: "steel_column_aisc",
    mark: "SC-1",
    callout: "W14X90 · ASTM A992",
    inputs: {
      steel_shape_key: "W14X90",
      steel_grade_key: "a992",
      design_method: "ASD",
      kx: 1,
      lx_ft: 30,
      ky: 1,
      ly_ft: 15,
      required_axial_kip: 560,
      required_mx_kipft: 0,
      required_my_kipft: 0,
      moments_fully_braced_confirmed: false,
    },
    calculationKind: "member_design",
    designMethod: "ASD",
    checks: [
      check("axial_compression", 560, 617.0738493046078, "kip", "AISC 360-22 Chapter E, Section E3", "x-axis; ASD"),
      check("axial_flexural_interaction", 0.9075088834684447, 1, "ratio", "AISC 360-22 Chapter H, Section H1", "H1-1a; ASD"),
    ],
    governing: "axial_compression",
    assumptions: ["Required moments are zero; the interaction value therefore equals the axial compression ratio."],
    limitations: [
      "Second-order analysis, stiffness reduction, local concentrated-load checks, torsional/flexural-torsional buckling, seismic provisions, fire, and connection design require separate verification.",
      "Moment interaction is limited to compact, fully braced rolled I-shapes (W, S, M, HP); unbraced beam-column moments are outside v1 scope.",
    ],
    intermediates: { governing_axis: "x", x_KL_over_r: 58.63192182410424, y_KL_over_r: 48.648648648648646, Fcr_ksi: 38.88729540900736, Pn_kip: 1030.513328338695 },
  },
  cfs_joist: {
    calculatorId: "cfs_joist",
    mark: "CJ-1",
    callout: "600S162-33 · SFIA 6 ft row",
    inputs: {
      cfs_total_load_key: "sfia:joist:600s162-33:fy33:6ft:total",
      cfs_live_load_key: "sfia:joist:600s162-33:fy33:6ft:live",
      span_ft: 6,
      web_stiffeners_provided: false,
      dead_load_plf: 20,
      live_load_plf: 40,
    },
    calculationKind: "published_capacity_check",
    designMethod: "ASD",
    checks: [
      check("total_uniform_load", 60, 168, "plf", "SFIA v2025.1 joist total-load table", "service ASD"),
      check("live_uniform_load_L_over_360", 40, 168, "plf", "SFIA v2025.1 joist live-load table", "service ASD"),
    ],
    governing: "total_uniform_load",
    assumptions: ["The selected total- and live-load rows are the matching 600S162-33, Fy 33 ksi, 6 ft entries."],
    limitations: [
      "Concentrated, tapered, partial, and continuous-span loads require a different analysis path.",
      "Web crippling at conditions outside the selected published row is not checked.",
    ],
    intermediates: { dead_load_plf: 20, live_load_plf: 40, total_load_plf: 60, span_ft: 6, yield_strength_ksi: 33 },
    reactions: [
      { label: "left", positionIn: 0, byType: { D: 60, L: 120 } },
      { label: "right", positionIn: 72, byType: { D: 60, L: 120 } },
    ],
  },
  cfs_stud_wall: {
    calculatorId: "cfs_stud_wall",
    mark: "CST-1",
    callout: "362S137-33 · 8 ft · 12 in o.c. · 5 psf",
    inputs: {
      cfs_stud_capacity_key: "sfia:combined:5psf:362s137-33:fy33:8ft:12oc",
      wall_height_ft: 8,
      stud_spacing_in: 12,
      lateral_pressure_psf: 5,
      bearing_stiffeners_provided: false,
      axial_load_kip: 1,
    },
    calculationKind: "published_capacity_check",
    designMethod: "ASD",
    checks: [
      check("combined_axial_and_lateral", 1, 1.46, "kip/stud", "SFIA v2025.1 combined axial/lateral table", "5 psf service lateral"),
    ],
    governing: "combined_axial_and_lateral",
    assumptions: [
      "Service-level ASD lateral pressure; axial load acts through the stud geometric center.",
      "Deflection is satisfied by the published table: the selected row is marked L/720 at the listed height, spacing, and lateral pressure.",
    ],
    limitations: [
      "End-support web crippling is not included in the SFIA combined-load value and must be checked separately.",
      "The exact published height, spacing, and lateral-pressure row is required; interpolation is not permitted.",
    ],
    intermediates: { wall_height_ft: 8, stud_spacing_in: 12, lateral_pressure_psf: 5, deflection_limit_met: "L/720" },
  },
  cfs_header: {
    calculatorId: "cfs_header",
    mark: "CH-1",
    callout: "2 × 550S162-33 · SFIA 3 ft row",
    inputs: {
      cfs_header_load_key: "sfia:header:550s162-33:fy33:3ft",
      span_ft: 3,
      web_stiffeners_provided: true,
      dead_load_plf: 200,
      live_load_plf: 500,
    },
    calculationKind: "published_capacity_check",
    designMethod: "ASD",
    checks: [
      check("uniform_load", 700, 931, "plf", "SFIA v2025.1 header table", "service ASD"),
      check("required_web_stiffeners", null, null, null, "SFIA v2025.1 header table note", "selected row"),
    ],
    governing: "uniform_load",
    assumptions: ["The selected published row is the two-member boxed-or-back-to-back header configuration."],
    limitations: ["Concentrated loads and openings with different bearing/configuration conditions require separate analysis."],
    intermediates: { span_ft: 3, dead_load_plf: 200, live_load_plf: 500, total_load_plf: 700, member_count: 2, web_stiffeners_required: true, web_stiffeners_provided: true },
    reactions: [
      { label: "left", positionIn: 0, byType: { D: 300, L: 750 } },
      { label: "right", positionIn: 36, byType: { D: 300, L: 750 } },
    ],
  },
  concrete_beam_aci: {
    calculatorId: "concrete_beam_aci",
    mark: "RCB-1",
    callout: "12 × 24 in · 4-#8",
    inputs: {
      concrete_material_key: "material:nrmca_benchmark_normalweight_4000psi",
      reinforcement_material_key: "material:reinforcing_bar_grade_a615_60",
      reinforcement_profile_key: "material_profile:rebar_size_profiles_us_inch_pound:rebar_profile_no_8",
      width_in: 12,
      depth_in: 24,
      cover_in: 1.5,
      tension_bar_count: 4,
      maximum_aggregate_size_in: 0.75,
      stirrup_diameter_in: 0.375,
      stirrup_legs: 2,
      stirrup_bar_area_in2: 0.11,
      stirrup_spacing_in: 8,
      factored_moment_kipft: 200,
      factored_shear_kip: 40,
    },
    calculationKind: "member_design",
    designMethod: "Strength design",
    checks: [
      check("flexure", 200, 274.46691176470586, "kip-ft", "ACI 318-19 Sections 9.6.1, 21.2, 22.2", "factored"),
      check("minimum_longitudinal_reinforcement", 0.865, 3.16, "in2", "ACI 318-19 Section 9.6.1.2", "detailing"),
      check("maximum_longitudinal_reinforcement", 3.16, 4.6471570512820515, "in2", "ACI 318-19 Section 9.3.3.1 and Table 21.2.2", "detailing"),
      check("single_layer_bar_fit", 7, 8.25, "in", "ACI 318-19 Section 25.2.1", "detailing"),
      check("one_way_shear", 40, 51.37926908441082, "kip", "ACI 318-19 Sections 9.6.3, 21.2, 22.5", "factored"),
      check("minimum_shear_reinforcement", 0.01, 0.0275, "in2/in", "ACI 318-19 Section 9.6.3", "detailing"),
      check("stirrup_spacing", 8, 10.8125, "in", "ACI 318-19 Section 9.7.6.2.2", "detailing"),
    ],
    governing: "single_layer_bar_fit",
    assumptions: ["Normalweight 4,000 psi concrete and Grade 60 #8 tension bars resolve from the selected catalog records."],
    limitations: [
      "Service deflection, crack control, development/splices, durability, fire, seismic detailing, and construction-stage checks require separate verification.",
      "The entered tension bars are checked as one layer; bundled or multi-layer reinforcement requires a different detailing path.",
      "Deep beams, T-beams, doubly reinforced beams, lightweight concrete, and reinforcement above Grade 60 are outside v1 scope.",
    ],
    intermediates: { d_in: 21.625, As_in2: 3.16, phi: 0.9, phi_mn_kipft: 274.46691176470586, bar_group_width_in: 7, width_inside_stirrups_in: 8.25 },
  },
  continuous_footing: {
    calculatorId: "continuous_footing",
    mark: "CF-1",
    callout: "4 ft × 18 in strip · #8 @ 8 in",
    inputs: {
      concrete_material_key: "material:nrmca_benchmark_normalweight_4000psi",
      reinforcement_material_key: "material:reinforcing_bar_grade_a615_60",
      reinforcement_profile_key: "material_profile:rebar_size_profiles_us_inch_pound:rebar_profile_no_8",
      footing_width_ft: 4,
      thickness_in: 18,
      wall_width_in: 8,
      cover_in: 3,
      bar_spacing_in: 8,
      dead_line_load_kipft: 5,
      live_line_load_kipft: 2,
      allowable_bearing_ksf: 2,
    },
    calculationKind: "foundation_design",
    designMethod: "Strength design plus service bearing",
    checks: [
      check("soil_bearing", 1.75, 2, "ksf", "Project geotechnical allowable bearing", "D+L service"),
      check("one_way_shear", 1.054166666666667, 16.507089386078942, "kip/ft", "ACI 318-19 Chapters 13, 21, 22, and 25", "factored"),
      check("footing_flexure", 3.1944444444444446, 72.67491727941176, "kip-ft/ft", "ACI 318-19 Chapters 13, 21, 22, and 25", "factored"),
      check("transverse_flexural_reinforcement", 0.3888, 1.185, "in2/ft", "ACI 318-19 Chapters 13, 21, 22, and 25", "detailing"),
    ],
    governing: "soil_bearing",
    assumptions: ["The wall is concentric on a one-foot transverse strip."],
    limitations: [
      "Footing self-weight, overburden, and buoyancy are not generated; include their applicable net effect in the entered dead line load and use a compatible geotechnical bearing convention.",
      "Longitudinal wall/footing behavior and reinforcement, eccentricity, sliding, settlement, wall dowels, and reinforcement development/detailing require separate verification.",
    ],
    intermediates: { service_q_ksf: 1.75, qu_ksf: 2.3, cantilever_projection_ft: 1.6666666666666667, d_in: 14.5, Mu_kipft_per_ft: 3.1944444444444446 },
  },
  pad_footing: {
    calculatorId: "pad_footing",
    mark: "PF-1",
    callout: "12 × 12 × 2.5 ft · 24 in column",
    inputs: {
      concrete_material_key: "material:nrmca_benchmark_normalweight_4000psi",
      column_location: "interior",
      reinforcement_material_key: "material:reinforcing_bar_grade_a615_60",
      reinforcement_profile_key: "material_profile:rebar_size_profiles_us_inch_pound:rebar_profile_no_8",
      footing_length_ft: 12,
      footing_width_ft: 12,
      thickness_in: 30,
      column_length_in: 24,
      column_width_in: 24,
      cover_in: 3,
      bar_count_x: 13,
      bar_count_y: 13,
      dead_load_kip: 541,
      live_load_kip: 194,
      allowable_dead_ksf: 4,
      allowable_dead_live_ksf: 5.6,
    },
    calculationKind: "foundation_design",
    designMethod: "Strength design plus service bearing",
    checks: [
      check("soil_bearing_dead", 3.7569444444444446, 4, "ksf", "Project geotechnical allowable bearing", "D service"),
      check("soil_bearing_dead_live", 5.104166666666667, 5.6, "ksf", "Project geotechnical allowable bearing", "D+L service"),
      check("one_way_shear_x", 229.90416666666664, 348.3565070441487, "kip", "ACI 318-19 Chapters 13, 21, 22, and 25", "factored"),
      check("one_way_shear_y", 229.90416666666664, 348.3565070441487, "kip", "ACI 318-19 Chapters 13, 21, 22, and 25", "factored"),
      check("two_way_punching_shear", 846.2097656249999, 957.9803943714087, "kip", "ACI 318-19 Chapters 13, 21, 22, and 25", "factored"),
      check("footing_flexure_x", 999.5833333333331, 1149.3998988970588, "kip-ft", "ACI 318-19 Chapters 13, 21, 22, and 25", "factored"),
      check("footing_flexure_y", 999.5833333333331, 1149.3998988970588, "kip-ft", "ACI 318-19 Chapters 13, 21, 22, and 25", "factored"),
      check("flexural_reinforcement_x", 8.901359467325292, 10.27, "in2", "ACI 318-19 Chapters 13, 21, 22, and 25", "detailing"),
      check("flexural_reinforcement_y", 8.901359467325292, 10.27, "in2", "ACI 318-19 Chapters 13, 21, 22, and 25", "detailing"),
      check("column_bearing", 959.5999999999999, 2545.92, "kip", "ACI 318-19 Section 22.8", "factored"),
    ],
    governing: "soil_bearing_dead",
    assumptions: ["The column is concentric and interior; service pressure is uniform."],
    limitations: [
      "Footing self-weight, overburden, and buoyancy are not generated; include their applicable net effect in the entered dead load and use a compatible geotechnical bearing convention.",
      "Eccentricity, biaxial soil pressure, sliding, settlement, punching reinforcement, dowel force transfer/development, and reinforcement development/detailing require separate verification.",
    ],
    intermediates: { service_q_dead_ksf: 3.7569444444444446, service_q_dead_live_ksf: 5.104166666666667, qu_ksf: 6.663888888888888, d_in: 25.5, punching_Vu_kip: 846.2097656249999 },
  },
  masonry_bearing_wall_tms: {
    calculatorId: "masonry_bearing_wall_tms",
    mark: "MW-1",
    callout: "8 in CMU · #4 @ 24 in centered",
    inputs: {
      masonry_fm_key: "concrete_m_or_s_2000",
      masonry_capacity_key: "cmha:tek14-19b:wall:8in:no4:24oc:centered",
      wall_height_ft: 12,
      lateral_pressure_psf: 20,
      service_axial_load_plf: 0,
    },
    calculationKind: "published_capacity_check",
    designMethod: "ASD",
    checks: [
      check("out_of_plane_flexure", 4320, 11130, "lb-in/ft", "CMHA TEK 14-19B Table 3a", "service ASD"),
      check("out_of_plane_shear", 120, 2235, "lb/ft", "CMHA TEK 14-19B Table 3a", "service ASD"),
    ],
    governing: "out_of_plane_flexure",
    assumptions: ["The selected published capacity row takes service axial load as zero."],
    limitations: [
      "The calculator does not interpolate wall thickness, reinforcement, f'm, or section configurations.",
      "Axial load, fire, seismic detailing, development/splices, concentrated loads, and construction-stage stability require separate verification.",
    ],
    intermediates: { wall_height_ft: 12, lateral_pressure_psf: 20, service_axial_load_plf: 0, simple_span_moment_lb_in_per_ft: 4320, simple_span_shear_lb_per_ft: 120 },
  },
  masonry_shear_wall_tms: {
    calculatorId: "masonry_shear_wall_tms",
    mark: "MSW-1",
    callout: "8 in reinforced CMU · 16 × 10 ft",
    inputs: {
      masonry_material_key: "masonry:astm_c90_cmu_generic",
      masonry_profile_key: "masonry_profile:cmu_module_8x8x16",
      masonry_capacity_key: "seau:bennett:tms402-16:shear-wall:8in:16ft:no4-pattern",
      wall_length_ft: 16,
      wall_height_ft: 10,
      required_axial_kip: 18.6,
      required_overturning_moment_kipft: 500,
      required_in_plane_shear_kip: 50,
    },
    calculationKind: "member_design",
    designMethod: "Strength",
    checks: [
      check("in_plane_axial_flexure", 500, 541.0010570304819, "kip-ft", "Slides 72-88 - interaction and shear examples", "Strength"),
      check("masonry_in_plane_shear", 50, 56.208266439984044, "kip", "Slides 72-88 - interaction and shear examples, TMS 402-16 9.3.4.1.2", "Strength"),
      check("base_shear_friction", 50, 56.17249999999999, "kip", "Slides 72-88 - interaction and shear examples, published shear-friction interpolation", "Strength"),
    ],
    governing: "in_plane_axial_flexure",
    assumptions: ["The material, unit profile, reinforcement, and 16 × 10 ft geometry match the selected published row."],
    limitations: [
      "No interpolation of wall geometry, reinforcement, f'm, grout pattern, or section properties is permitted.",
      "The P-M solver is limited to the source-verified tension branch through c=54 in and phiPn=180.1 kip.",
      "Seismic system eligibility and detailing, drift, second-order effects, development/splices, collectors, connections, foundations, and boundary elements require separate verification.",
    ],
    intermediates: { neutral_axis_in: 15.824729596853487, design_moment_at_required_axial_kipft: 541.0010570304819, masonry_design_kip: 56.208266439984044, shear_friction_design_kip: 56.17249999999999 },
  },
  masonry_lintel_tms: {
    calculatorId: "masonry_lintel_tms",
    mark: "ML-1",
    callout: "12 × 24 CMU lintel · 5.7 ft",
    inputs: {
      masonry_fm_key: "concrete_m_or_s_2000",
      masonry_capacity_key: "cmha:tek17-01d:lintel:12x24:no4:1bar:cover3",
      effective_span_ft: 5.7,
      uniform_load_plf: 1264,
      uniform_load_type: "D",
      triangular_peak_load_plf: 200,
      triangular_peak_load_type: "S",
      midpoint_load_lb: 500,
      midpoint_load_type: "L",
    },
    calculationKind: "member_design",
    designMethod: "ASD",
    checks: [
      check("lintel_flexure", 76649.04000000001, 122872, "lb-in", "CMHA TEK 17-01D Case 2", "service ASD"),
      check("lintel_shear", 4137.4, 10256, "lb", "CMHA TEK 17-01D Case 2", "service ASD"),
    ],
    governing: "lintel_flexure",
    assumptions: ["The selected capacity row matches the entered 5.7 ft effective span."],
    limitations: [
      "Arching may only be represented by the explicitly entered load model; concentrated loads away from midspan, continuity, axial force, deflection, development, and bearing require separate verification.",
      "Capacity interpolation between lintel configurations is not permitted.",
    ],
    intermediates: { effective_span_ft: 5.7, uniform_moment_lbft: 5133.42, triangular_moment_lbft: 541.5, midpoint_moment_lbft: 712.5, total_moment_lb_in: 76649.04000000001, support_shear_lb: 4137.4 },
    reactions: [
      { label: "left", positionIn: 0, byType: { D: 3602.4, L: 250, S: 285 } },
      { label: "right", positionIn: 68.4, byType: { D: 3602.4, L: 250, S: 285 } },
    ],
  },
};

export const SECTION_PROPERTIES_HELP_FIXTURES = {
  rectangle: {
    mark: "SP-1",
    callout: "6 × 12 in rectangle",
    inputs: { shape_type: "rectangle", width_in: 6, depth_in: 12 } satisfies CalcInputs,
    values: { area_in2: 72, ix_in4: 864, iy_in4: 216, cx_in: 3, cy_in: 6, sx_in3: 144, sy_in3: 72, rx_in: 3.4641016151377544, ry_in: 1.7320508075688772 },
  },
  iSection: {
    mark: "SP-2",
    callout: "12 × 8 in I-section",
    inputs: { shape_type: "i_section", depth_in: 12, flange_width_in: 8, flange_thickness_in: 0.75, web_thickness_in: 0.5 } satisfies CalcInputs,
    values: { area_in2: 17.25, ix_in4: 428.484375, iy_in4: 64.109375, cx_in: 4, cy_in: 6, sx_in3: 71.4140625, sy_in3: 16.02734375, rx_in: 4.983941604096448, ry_in: 1.927818847103217 },
  },
} as const;
